import { Component, computed, DestroyRef, effect, inject, output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatChipsModule } from "@angular/material/chips";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { InputsModule } from "@progress/kendo-angular-inputs";
import { KENDO_BUTTONS } from "@progress/kendo-angular-buttons";
import { InstructionBuilderService } from "../services/instruction-builder.service";
import { KeywordsControllerComponent } from "../../../../../shared/keywords-controller/keywords-controller.component";
import { KeywordService } from "../../../../../services/keyword.service";

@Component({
  selector: "app-instruction-definition",
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatSlideToggleModule,
    InputsModule,
    KENDO_BUTTONS,
    KeywordsControllerComponent
],
  templateUrl: "./instruction-definition.component.html",
  styleUrl: "./instruction-definition.component.scss",
})
export class InstructionDefinitionComponent {
  instructionForm!: FormGroup;

  /** Emits the latest value of the name field on every change. */
  readonly nameChange = output<string>();

  // Hardcoded keywords (kept for reference/fallback)
  hardcodedKeywordStrings = [
    "Proposal",
    "Contract",
    "Invoice",
    "Report",
    "Meeting Notes",
    "Presentation",
    "Budget",
    "Plan",
    "Summary",
    "Analysis",
  ];

  // Services
  #instructionBuilderService = inject(InstructionBuilderService);
  #keywordService = inject(KeywordService);
  #destroyRef = inject(DestroyRef);

  // Merge API keywords with legacy hardcoded strings
  allKeywords = computed(() =>
    this.#keywordService.mergeWithLegacyStrings(this.hardcodedKeywordStrings)
  );
  isLoadingKeywords = this.#keywordService.isLoading;

  constructor(private fb: FormBuilder) {
    effect(() => {
      this.instructionForm = this.fb.group({
        name: new FormControl(
          this.#instructionBuilderService.instruction().details.name,
          [Validators.required]
        ),
        description: new FormControl(
          this.#instructionBuilderService.instruction().details.description
        ),
        keywords: new FormControl<string[]>(
          this.#instructionBuilderService.instruction().details.keywords
        ),
      });

      if (this.#instructionBuilderService.instruction().id) {
        this.instructionForm.get("name")?.setValue(this.#instructionBuilderService.instruction().details.name);
        this.instructionForm.get("description")?.setValue(this.#instructionBuilderService.instruction().details.description);
        this.instructionForm.get("keywords")?.setValue(this.#instructionBuilderService.instruction().details.keywords);
      }

      this.instructionForm.get('name')!.valueChanges.pipe(
        takeUntilDestroyed(this.#destroyRef)
      ).subscribe(name => this.nameChange.emit(name ?? ''));
    });

    // Load keywords from API
    this.#keywordService.loadKeywords();
  }

    /**
   * Marks all controls in the instruction form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the InstructionBuilderComponent.
   */
  markFormAsTouched(): void {
    this.instructionForm.markAllAsTouched();
  }

  /**
   * Validates the form and returns whether it's valid or not.
   * Used by the parent component to determine if this step is complete.
   * @returns A boolean indicating whether the form is valid.
   */
  isValid(): boolean {
    if (!this.instructionForm) {
      return false;
    }

    // Reset form errors
    this.instructionForm.get("name")?.setErrors(null);

    this.instructionForm.markAllAsTouched();
    if (this.instructionForm.get("name")?.value === "") {
      this.instructionForm.get("name")?.setErrors({ required: true });
    }
    return this.instructionForm.valid;
  }
}
